import React from 'react';
import { SlotRenderer } from 'mpf-slots-frontend/components';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import config from '@plone/volto/registry';
import { isSlotAvailable, slotHasBlockType } from 'mpf-slots-frontend/helpers';
import { MobileSidebarSheet } from 'volto-ministerio-publico-federal/components/UI/MobileSidebarSheet';
import { UnidadeResponsavel } from 'volto-ministerio-publico-federal/components/UnidadeResponsavel';
import { getAlignmentClassName } from 'volto-ministerio-publico-federal/components/UnidadeResponsavel/alignment';
import { ROUTES } from 'volto-ministerio-publico-federal/config/routes.constants';

// Layouts desenhados sem lateral: a página ocupa a largura toda e a Área
// responsável vai para o rodapé, como quando o bloco não está no slot.
const LAYOUTS_SEM_LATERAL = ['servicos_view'];

// Nesses layouts o card segue o desenho da página: tudo numa linha e o
// título "Area responsável".
const PROPS_UNIDADE_SEM_LATERAL = {
  disposicao: 'linha',
  titulo: 'Área responsável',
};

const ContentContainer = ({ children, content }) => {
  const pathname = useLocation().pathname;
  const { slots } = config;
  const slotData = useSelector((state) => {
    return state.slots?.data || {};
  });

  const available = (name) => {
    if (!slots[name]) {
      return null;
    }
    const props = { pathname, slotData, slotName: name, slots, content };
    return slots[name].available
      ? slots[name].available(props)
      : isSlotAvailable(props);
  };

  const semLateral = LAYOUTS_SEM_LATERAL.includes(content?.layout);
  const hasLeftSlot = !semLateral && available('asideLeftSlot');
  const hasRightSlot = available('asideRightSlot');

  // RF03 + RF04: a Área responsável é um componente independente da
  // lateral (bloco `mpfUnidadeResponsavel`), mas não é opcional — desligá-la
  // ali não a faz sumir, manda o card para o rodapé do conteúdo. Por isso a
  // pergunta aqui é "o bloco está na lateral?", e não "existe lateral?".
  const unidadeNaLateral =
    !semLateral &&
    slotHasBlockType({
      slotName: 'asideLeftSlot',
      pathname,
      slotData,
      type: 'mpfUnidadeResponsavel',
    });

  // O alinhamento à coluna de leitura só vale quando a página NÃO tem
  // lateral: com ela, o texto é deslocado pela sidebar e o grid de 12 colunas
  // que `alignment.ts` reproduz deixa de descrever a página (ver
  // `useHasSidebar`). Nesse caso o card acompanha a largura do container.
  const alignClassName = hasLeftSlot
    ? null
    : getAlignmentClassName(
        semLateral ? { ...content, largura_total: true } : content,
      );

  // A listagem central de notícias reúne todas as unidades — não há uma
  // "unidade responsável" única pela página, então o card de rodapé não faz
  // sentido ali e é ocultado. (Trailing slash normalizado.)
  const naListagemDeNoticias =
    (pathname || '').replace(/\/+$/, '') === ROUTES.COMUNICACAO_NOTICIAS;

  let classContent = hasLeftSlot ? 'mpf-internal__content' : '__content';
  let classContents = hasLeftSlot ? 'mpf-internal__contents' : '__contents';

  return (
    <>
      {![
        'News',
        'Noticia',
        'News Item',
        'Plone Site',
        'Home',
        'Highlights',
      ].includes(content?.['@type']) ? (
        <section className="__container">
          <div className={classContent}>
            {hasLeftSlot || hasRightSlot ? (
              <>
                {hasLeftSlot && (
                  <div
                    className="mpf-internal__sidebar"
                    id="internal-left-aside"
                  >
                    {/* O card da unidade, quando está na lateral, é o bloco
                        `mpfUnidadeResponsavel` renderizado aqui dentro pelo
                        SlotRenderer, na posição em que o editor o colocou —
                        e conta para o teto de 5 componentes (RNF04). */}
                    <MobileSidebarSheet
                      label="Menu da página"
                      closeKey={content?.['@id']}
                    >
                      <SlotRenderer name="asideLeftSlot" metadata={content} />
                    </MobileSidebarSheet>
                  </div>
                )}
                <div className={classContents}>{children}</div>
                {hasRightSlot && (
                  <div as="aside" className="aside-right-slot" width={3}>
                    <SlotRenderer name="asideRightSlot" metadata={content} />
                  </div>
                )}
              </>
            ) : (
              <>{children}</>
            )}
          </div>
          {/* RF03: quando o bloco não está na lateral, o card vai no fim da
              página — irmão do bloco de conteúdo, não dentro dele. É o que
              garante que toda página continue identificando quem responde por
              ela, mesmo que o editor use as 5 vagas da lateral com outros
              componentes. O wrapper reproduz o grid de 12 colunas do
              `InternalLayout` para o card ficar na mesma coluna do texto; nos
              tipos que não têm essa coluna ele vem nulo e o card ocupa a
              largura do container (ver `alignment.ts`).

              Exceção: a listagem central de notícias reúne todas as unidades,
              então não há uma unidade responsável única — o card é ocultado
              ali (`naListagemDeNoticias`). */}
          {!unidadeNaLateral &&
            !naListagemDeNoticias &&
            (alignClassName ? (
              <div className={alignClassName}>
                <UnidadeResponsavel
                  content={content}
                  variant="full"
                  {...(semLateral ? PROPS_UNIDADE_SEM_LATERAL : {})}
                />
              </div>
            ) : (
              <UnidadeResponsavel
                content={content}
                variant="full"
                {...(semLateral ? PROPS_UNIDADE_SEM_LATERAL : {})}
              />
            ))}
        </section>
      ) : (
        <>{children}</>
      )}
    </>
  );
};

export default ContentContainer;
